import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "date-picker-container" }
const _hoisted_2 = { class: "date-picker-wrapper" }
const _hoisted_3 = {
  key: 0,
  class: "aq-in-range-bg"
}

import { computed, ref, watch, nextTick } from 'vue';
    import { useI18n } from 'vue-i18n';
    import locales from '@/locales/date-picker-locales.json';
    import moment from 'moment';
    import type { Moment } from 'moment';
    import { formatDate } from '@/utilities/utils';

    type DaySlotItem = {
        date: Date;
        formatted: string;
        isAdjacent: boolean;
        isDisabled: boolean;
        isEnd: boolean;
        isHidden: boolean;
        isSame: boolean;
        isSelected: boolean;
        isStart: boolean;
        isToday: boolean;
        isWeekEnd: boolean;
        isWeekStart: boolean;
        isoDate: string;
        localized: string;
        month: number;
        year: number;
    };

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AQDatepicker',
  props: {
    modelValue: {},
    fieldProps: {},
    required: { type: Boolean },
    label: {},
    landscape: { type: Boolean },
    format: {},
    hideDetails: { type: Boolean },
    color: {},
    disabled: { type: Boolean },
    min: {},
    max: {},
    clearable: { type: Boolean },
    dense: { type: Boolean },
    multiple: { type: Boolean },
    singleBound: {}
  },
  emits: ["update:modelValue", "dayBounds", "boundValue", "blur"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const { locale, t } = useI18n();

    const isMenuOpen = ref(false);
    const localValue = ref<string[] | string>(props.multiple ? [] : '');
    const rangeStart = ref<string | null>(null);
    const rangeEnd = ref<string | null>(null);
    const errorMessage = ref<string>('');
    const prevSelection = ref<string[]>([]);
    const restartOnNextClick = ref<boolean>(false);
    const datePickerKey = ref<number>(0);

    // Initialize displayMonth and displayYear based on modelValue if available
    const getInitialMonthYear = () => {
        if (props.modelValue) {
            if (Array.isArray(props.modelValue)) {
                const firstDate = props.modelValue[0];
                if (firstDate) {
                    const m = moment(firstDate);
                    if (m.isValid()) {
                        return { month: m.month(), year: m.year() };
                    }
                }
            } else if (typeof props.modelValue === 'string') {
                const m = moment(props.modelValue);
                if (m.isValid()) {
                    return { month: m.month(), year: m.year() };
                }
            }
        }
        return { month: moment().month(), year: moment().year() };
    };

    const initial = getInitialMonthYear();
    const displayMonth = ref<number>(initial.month);
    const displayYear = ref<number>(initial.year);
    const inRangeSet = ref<Set<string>>(new Set());

    const toYMD = (input: string | Date | null | undefined): string => {
        if (!input) return '';
        if (typeof input === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
            const m = moment(input);
            return m.isValid() ? m.format('YYYY-MM-DD') : '';
        }
        const m = moment(input);
        return m.isValid() ? m.format('YYYY-MM-DD') : '';
    };
    const recomputeInRange = () => {
        const set = new Set<string>();
        if (!rangeStart.value || !rangeEnd.value) {
            inRangeSet.value = set;
            return;
        }
        let start = moment(rangeStart.value, 'YYYY-MM-DD');
        let end = moment(rangeEnd.value, 'YYYY-MM-DD');
        if (end.isBefore(start)) {
            const tmp = start;
            start = end;
            end = tmp;
        }
        const d = start.clone().add(1, 'day');
        while (d.isBefore(end, 'day')) {
            set.add(d.format('YYYY-MM-DD'));
            d.add(1, 'day');
        }
        inRangeSet.value = set;
    };

    watch([rangeStart, rangeEnd], () => {
        recomputeInRange();
        emitDayBounds();
    });

    const datePickerLocale = computed(
        () => locales[locale.value.toLowerCase() as keyof typeof locales] || 'en-US',
    );

    const singleBound = computed<'start' | 'end'>(() =>
        props.singleBound === 'end' ? 'end' : 'start',
    );

    const emitDayBounds = () => {
        let start: moment.Moment | null = null;
        let end: moment.Moment | null = null;

        if (props.multiple) {
            if (rangeStart.value) {
                const ms = moment(rangeStart.value, 'YYYY-MM-DD');
                if (ms.isValid()) start = ms.clone().startOf('day');
            }
            if (rangeEnd.value) {
                const me = moment(rangeEnd.value, 'YYYY-MM-DD');
                if (me.isValid()) end = me.clone().endOf('day');
            } else if (rangeStart.value) {
                const me = moment(rangeStart.value, 'YYYY-MM-DD');
                if (me.isValid()) end = me.clone().endOf('day');
            }
        } else if (typeof localValue.value === 'string' && localValue.value) {
            const m = moment(localValue.value, 'YYYY-MM-DD');
            if (m.isValid()) {
                start = m.clone().startOf('day');
                end = m.clone().endOf('day');
            }
        }

        emit('dayBounds', { startOfDay: start, endOfDay: end });
    };

    const emitChosenBound = () => {
        if (props.multiple) return;
        let chosen: Moment | null = null;
        if (typeof localValue.value === 'string' && localValue.value) {
            const m = moment(localValue.value, 'YYYY-MM-DD');
            if (m.isValid()) {
                chosen =
                    singleBound.value === 'end' ? m.clone().endOf('day') : m.clone().startOf('day');
            }
        }
        emit('boundValue', chosen);
    };

    watch(
        () => localValue.value,
        () => {
            if (!props.multiple) {
                emitDayBounds();
            }
        },
    );

    const buildInclusiveRange = (startStr: string, endStr: string): string[] => {
        const arr: string[] = [];
        let start = moment(startStr, 'YYYY-MM-DD');
        let end = moment(endStr, 'YYYY-MM-DD');
        if (end.isBefore(start)) {
            const tmp = start;
            start = end;
            end = tmp;
        }
        const d = start.clone();
        while (d.isSameOrBefore(end, 'day')) {
            arr.push(d.format('YYYY-MM-DD'));
            d.add(1, 'day');
        }
        return arr;
    };

    watch(
        () => props.modelValue,
        (newValue) => {
            if (props.multiple) {
                if (Array.isArray(newValue)) {
                    const parsed = newValue
                        .filter((d): d is string => !!d)
                        .map((date) => toYMD(date));
                    rangeStart.value = parsed[0] || null;
                    rangeEnd.value = parsed[1] || null;
                    localValue.value =
                        rangeStart.value && rangeEnd.value
                            ? buildInclusiveRange(rangeStart.value, rangeEnd.value)
                            : [
                                  ...(rangeStart.value ? [rangeStart.value] : []),
                                  ...(rangeEnd.value ? [rangeEnd.value] : []),
                              ];
                    prevSelection.value = Array.isArray(localValue.value)
                        ? [...(localValue.value as string[])]
                        : [];
                } else {
                    rangeStart.value = null;
                    rangeEnd.value = null;
                    localValue.value = [];
                    prevSelection.value = [];
                }
                if (rangeStart.value && rangeEnd.value) {
                    const start = moment(rangeStart.value, 'YYYY-MM-DD');
                    const end = moment(rangeEnd.value, 'YYYY-MM-DD');
                    errorMessage.value = end.isBefore(start) ? t('generic.dateRangeError') : '';
                } else {
                    errorMessage.value = '';
                }
            } else {
                localValue.value = typeof newValue === 'string' ? toYMD(newValue) : '';
                errorMessage.value = '';
            }
        },
        { immediate: true },
    );

    watch(
        () => isMenuOpen.value,
        (open, oldValue) => {
            if (open && props.multiple) {
                restartOnNextClick.value = true;
                const highlights: string[] = [];
                if (rangeStart.value && rangeEnd.value) {
                    highlights.push(...buildInclusiveRange(rangeStart.value, rangeEnd.value));
                } else {
                    if (rangeStart.value) highlights.push(rangeStart.value);
                    if (rangeEnd.value) highlights.push(rangeEnd.value);
                }
                localValue.value = highlights;
                prevSelection.value = [...highlights];
            } else if (!open) {
                restartOnNextClick.value = false;
            }
            if (oldValue === true && open === false) {
                emit('blur');
            }
        },
    );

    const handleDateUpdate = async (value: string[] | string | Date[] | Date) => {
        if (props.multiple && Array.isArray(value)) {
            const current = (value as Array<string | Date>).map((d) => toYMD(d));
            const added = current.find((d) => !prevSelection.value.includes(d));
            const removed = prevSelection.value.find((d) => !current.includes(d));
            const diffs = current.filter((d) => !prevSelection.value.includes(d));

            if (restartOnNextClick.value) {
                const sameAsBefore =
                    current.length === prevSelection.value.length &&
                    current.every((d) => prevSelection.value.includes(d)) &&
                    prevSelection.value.every((d) => current.includes(d));
                if (sameAsBefore) return;

                let clicked = added ?? removed;
                if (!clicked) {
                    const diff = current.filter((d) => !prevSelection.value.includes(d));
                    clicked = diff[0] ?? current[current.length - 1];
                }
                if (clicked) {
                    rangeStart.value = clicked;
                    rangeEnd.value = null;
                    localValue.value = [];
                    prevSelection.value = [];
                    await nextTick();
                    localValue.value = [rangeStart.value];
                    prevSelection.value = [rangeStart.value];
                    recomputeInRange();
                    datePickerKey.value += 1;
                    errorMessage.value = '';
                    restartOnNextClick.value = false;
                    emit('update:modelValue', [rangeStart.value, null]);
                    return;
                }
                return;
            }

            prevSelection.value = [...current];

            // Special case: user clicked the same day as the start again -> interpret as same-day range
            if (
                current.length === 0 &&
                rangeStart.value &&
                !rangeEnd.value &&
                removed === rangeStart.value
            ) {
                rangeEnd.value = rangeStart.value;
                localValue.value = buildInclusiveRange(rangeStart.value, rangeEnd.value);
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [rangeStart.value, rangeEnd.value]);
                isMenuOpen.value = false;
                return;
            }

            if (current.length === 0) {
                rangeStart.value = null;
                rangeEnd.value = null;
                localValue.value = [];
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [null, null]);
                return;
            }

            if (current.length === 1) {
                rangeStart.value = current[0];
                rangeEnd.value = null;
                localValue.value = [rangeStart.value];
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [rangeStart.value, null]);
                return;
            }

            if (rangeStart.value && !rangeEnd.value && (added || diffs.length)) {
                let candidate = added as string | undefined;
                if (!candidate && diffs.length) {
                    const startM = moment(rangeStart.value, 'YYYY-MM-DD');
                    candidate = diffs.reduce((furthest, d) => {
                        const df = Math.abs(moment(d, 'YYYY-MM-DD').diff(startM, 'days'));
                        const ff = Math.abs(moment(furthest, 'YYYY-MM-DD').diff(startM, 'days'));
                        return df > ff ? d : furthest;
                    }, diffs[0]);
                }
                const candidateStr = candidate as string;
                const start = moment(rangeStart.value, 'YYYY-MM-DD');
                const candidateMoment = moment(candidateStr, 'YYYY-MM-DD');

                if (candidateMoment.isBefore(start)) {
                    rangeStart.value = candidateStr;
                    rangeEnd.value = null;
                    localValue.value = [rangeStart.value];
                    errorMessage.value = '';
                    recomputeInRange();
                    emit('update:modelValue', [rangeStart.value, null]);
                    return;
                }

                rangeEnd.value = candidateStr;
                localValue.value = buildInclusiveRange(rangeStart.value, rangeEnd.value);
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [rangeStart.value, rangeEnd.value]);
                isMenuOpen.value = false;
                return;
            }

            if (rangeStart.value && rangeEnd.value && added) {
                rangeStart.value = added;
                rangeEnd.value = null;
                localValue.value = [rangeStart.value];
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [rangeStart.value, null]);
                return;
            }

            if (removed) {
                if (removed === rangeStart.value) {
                    rangeStart.value = rangeEnd.value;
                    rangeEnd.value = null;
                    localValue.value = rangeStart.value ? [rangeStart.value] : [];
                    errorMessage.value = '';
                    recomputeInRange();
                    emit('update:modelValue', [rangeStart.value ? rangeStart.value : null, null]);
                    return;
                }
                if (removed === rangeEnd.value) {
                    rangeEnd.value = null;
                    localValue.value = rangeStart.value ? [rangeStart.value] : [];
                    errorMessage.value = '';
                    recomputeInRange();
                    emit('update:modelValue', [rangeStart.value ? rangeStart.value : null, null]);
                    return;
                }
            }

            if (current.length >= 2) {
                const sorted = [...current].sort();
                const start = sorted[0];
                const end = sorted[sorted.length - 1];
                rangeStart.value = start;
                rangeEnd.value = end;
                localValue.value = buildInclusiveRange(start, end);
                errorMessage.value = '';
                recomputeInRange();
                emit('update:modelValue', [start, end]);
                isMenuOpen.value = false;
            }
        } else {
            let str = '';
            if (value instanceof Date) {
                str = toYMD(value);
            } else if (typeof value === 'string') {
                str = toYMD(value);
            }
            localValue.value = str;
            errorMessage.value = '';
            emit('update:modelValue', str || null);
            isMenuOpen.value = false;
            // Immediately emit bounds for single-date mode
            emitDayBounds();
            emitChosenBound();
        }
    };

    const computedLabel = computed(() =>
        props.required && props.label ? `${props.label} *` : props.label || '',
    );

    const displayText = computed(() => {
        if (props.multiple) {
            if (rangeStart.value && rangeEnd.value) {
                return `${formatDate(rangeStart.value)} - ${formatDate(rangeEnd.value)}`;
            }
            if (rangeStart.value) {
                return `${formatDate(rangeStart.value)} -`;
            }
            return '';
        }
        if (typeof localValue.value === 'string' && localValue.value) {
            return formatDate(localValue.value);
        }
        return '';
    });

    const handleClear = () => {
        if (props.multiple) {
            rangeStart.value = null;
            rangeEnd.value = null;
            localValue.value = [];
            prevSelection.value = [];
            errorMessage.value = '';
            emit('update:modelValue', [null, null]);
        } else {
            localValue.value = '';
            errorMessage.value = '';
            emit('update:modelValue', null);
            emit('dayBounds', { startOfDay: null, endOfDay: null });
            emit('boundValue', null);
        }
    };

    const getItemIsoVisual = (item: DaySlotItem): string | null => {
        if (item.isoDate) return item.isoDate;
        if (item.date instanceof Date) {
            const out = toYMD(item.date);
            return out || null;
        }
        const dayNum = parseInt(item.localized, 10);
        if (!dayNum) return null;
        const m = moment({ year: item.year, month: item.month, date: dayNum });
        return m.isValid() ? m.format('YYYY-MM-DD') : null;
    };

    const isInRange = (item: DaySlotItem): boolean => {
        if (!props.multiple || !rangeStart.value || !rangeEnd.value) return false;
        const iso = getItemIsoVisual(item);
        if (!iso) return false;
        return inRangeSet.value.has(iso);
    };

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_date_picker = _resolveComponent("v-date-picker")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_v_menu, {
        modelValue: isMenuOpen.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isMenuOpen).value = $event)),
        "close-on-content-click": false,
        transition: "scale-transition",
        "offset-y": ""
      }, {
        activator: _withCtx(({ props: activatorProps }) => [
          _createVNode(_component_v_text_field, _mergeProps({
            "model-value": displayText.value,
            "prepend-inner-icon": "mdi-calendar",
            clearable: __props.clearable,
            color: __props.color || 'primary',
            disabled: __props.disabled,
            readonly: "",
            density: __props.dense ? 'compact' : 'default',
            label: computedLabel.value,
            "hide-details": __props.hideDetails,
            error: !!errorMessage.value,
            "error-messages": errorMessage.value,
            variant: "outlined",
            style: {"width":"100%"},
            "onClick:clear": handleClear
          }, { ...__props.fieldProps, ...activatorProps }), null, 16, ["model-value", "clearable", "color", "disabled", "density", "label", "hide-details", "error", "error-messages"])
        ]),
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_component_v_date_picker, {
            key: datePickerKey.value,
            modelValue: localValue.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((localValue).value = $event)),
              handleDateUpdate
            ],
            month: displayMonth.value,
            "onUpdate:month": _cache[1] || (_cache[1] = ($event: any) => ((displayMonth).value = $event)),
            year: displayYear.value,
            "onUpdate:year": _cache[2] || (_cache[2] = ($event: any) => ((displayYear).value = $event)),
            color: __props.color || 'primary',
            disabled: __props.disabled,
            locale: datePickerLocale.value,
            landscape: __props.landscape,
            "show-current": true,
            min: __props.min,
            max: __props.max,
            multiple: __props.multiple,
            style: {"width":"100%"}
          }, {
            day: _withCtx(({ props: dayProps, item }) => [
              _createElementVNode("div", {
                class: _normalizeClass(['aq-day-wrap', { 'aq-in-range-wrap': isInRange(item) }])
              }, [
                _createVNode(_component_v_btn, _mergeProps(dayProps, {
                  class: ['aq-day', { 'aq-in-range': isInRange(item) }],
                  style: 
                                    isInRange(item)
                                        ? { backgroundColor: 'rgba(0, 0, 0, 0.16)' }
                                        : undefined
                                
                }), {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString((item &&
                                        (item.localized ??
                                            item.text ??
                                            item.title ??
                                            item.label)) ??
                                    ''), 1)
                  ]),
                  _: 2
                }, 1040, ["class", "style"]),
                (isInRange(item))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3))
                  : _createCommentVNode("", true)
              ], 2)
            ]),
            _: 1
          }, 8, ["modelValue", "month", "year", "color", "disabled", "locale", "landscape", "min", "max", "multiple"]))
        ]),
        _: 1
      }, 8, ["modelValue"])
    ])
  ]))
}
}

})